import { Form, Formik, type FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import CustomCheckboxInput from '../../../components/Form/custom-checkbox-input';
import CustomTextInput from '../../../components/Form/custom-text-input';
import { EditMerchantSchema } from '../merchant.schema';
import { extractErrorsFromApi } from '../../../common/helpers';
import { type Merchant } from '../../../common/types';
import RenderErrorBag from '../../../components/render-error-bag';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useUpdateMerchantMutation } from '../merchant.api-slice';

interface IProps {
  isOpen: boolean;
  toggleEditModal: () => any;
  item: Merchant | null;
}

const EditMerchant: React.FC<IProps> = ({ item, isOpen, toggleEditModal }) => {
  const [updateMerchant, { isLoading, isError, error, isSuccess, reset }] = useUpdateMerchantMutation();
  const [errorBag, setErrorBag] = useState<string[]>([]);
  const { t } = useTranslation();
  const handleUpdateMerchant = async (values: Merchant, actions: FormikHelpers<Merchant>): Promise<any> => {
    if (item == null) {
      return;
    }
    await updateMerchant({ body: { ...values }, id: item.id });
    actions.resetForm();
    toggleEditModal();
  };

  useEffect(() => {
    if (isSuccess) {
      toast(t('merchants.edit.successMessage'));
      reset();
    }
    if (isError && error != null) {
      setErrorBag(extractErrorsFromApi(error));
    }
  }, [isError, isSuccess]);

  return (
    <>
      {isOpen && item != null ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-full my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 className="text-3xl font-semibold">{t('merchants.edit.title')}</h3>
                  <button
                    className="p-1 ml-auto border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                    onClick={toggleEditModal}
                  >
                    <AiOutlineClose size={20} />
                  </button>
                </div>
                <div className="relative p-6 flex-auto w-full">
                  {errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
                  {
                    <Formik initialValues={item} onSubmit={handleUpdateMerchant} validationSchema={EditMerchantSchema}>
                      {({ isSubmitting }) => (
                        <Form className="w-full flex flex-col items-start px-2" autoComplete="off">
                          <CustomTextInput
                            type="text"
                            label="Merchant Name"
                            name="name"
                            divClass="w-full flex flex-col "
                            labelClass="w-full text-left text-gray-600"
                            inputClass="w-full my-1 px-4 py-1 rounded-md"
                          />
                          <CustomTextInput
                            type="text"
                            label="Email"
                            name="email"
                            divClass="w-full flex flex-col"
                            labelClass="w-full text-left mt-2 text-gray-600"
                            inputClass="w-full my-1 px-4 py-1 rounded-md"
                          />
                          <CustomTextInput
                            type="text"
                            label="Phone"
                            name="phone"
                            divClass="w-full flex flex-col"
                            labelClass="w-full text-left mt-2 text-gray-600"
                            inputClass="w-full my-1 px-4 py-1 rounded-md"
                          />
                          <CustomTextInput
                            type="text"
                            label="Address"
                            name="address"
                            divClass="w-full flex flex-col"
                            labelClass="w-full text-left mt-2 text-gray-600"
                            inputClass="w-full my-1 px-4 py-1 rounded-md"
                          />
                          <CustomCheckboxInput
                            label="Active"
                            name="isActive"
                            divClass="w-full flex items-center"
                            labelClass="text-left mt-2 text-gray-600"
                            inputClass="p-2 mt-2 ml-4 rounded-md"
                          />
                          <div className="w-full flex space-x-2 py-6 mt-4 border-t border-solid border-slate-200 rounded-b">
                            <button
                              className="w-1/2 text-black px-5 py-2 rounded-lg bg-gray-200"
                              onClick={toggleEditModal}
                            >
                              {t('merchants.edit.cancelBtnLabel')}
                            </button>
                            <button
                              type="submit"
                              disabled={isLoading || isSubmitting}
                              className="w-1/2 text-white px-4 py-2 rounded-lg bg-[#1D8A43]"
                            >
                              {t('merchants.edit.saveBtnLabel')}
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </>
  );
};

export default EditMerchant;
